import axios from 'axios';
import { isTokenExpired, isTokenExpiringSoon } from '../utils/jwt';

const ACCESS_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';
const ACCESS_EXPIRES_KEY = 'accessTokenExpiresAt';
const REFRESH_EXPIRES_KEY = 'refreshTokenExpiresAt';

let refreshPromise = null;

function getApiBaseUrl() {
  return process.env.REACT_APP_BASE_URL;
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setSession({
  accessToken,
  token,
  refreshToken,
  user,
  accessTokenExpiresAt,
  refreshTokenExpiresAt,
}) {
  const resolvedAccess = accessToken || token;
  if (resolvedAccess) {
    localStorage.setItem(ACCESS_TOKEN_KEY, resolvedAccess);
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
  if (accessTokenExpiresAt) {
    localStorage.setItem(ACCESS_EXPIRES_KEY, accessTokenExpiresAt);
  }
  if (refreshTokenExpiresAt) {
    localStorage.setItem(REFRESH_EXPIRES_KEY, refreshTokenExpiresAt);
  }
}

export function clearSession() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem('user_type');
  localStorage.removeItem(ACCESS_EXPIRES_KEY);
  localStorage.removeItem(REFRESH_EXPIRES_KEY);
}

export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  const response = await axios.post(
    `${getApiBaseUrl()}/auth/refresh`,
    { refreshToken },
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Platform': 'web',
      },
    },
  );

  const data = response.data || {};
  const nextAccessToken = data.accessToken || data.token;
  setSession({
    accessToken: nextAccessToken,
    refreshToken: data.refreshToken,
    accessTokenExpiresAt: data.accessTokenExpiresAt,
    refreshTokenExpiresAt: data.refreshTokenExpiresAt,
  });

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('sessionRefreshed', { detail: { accessToken: nextAccessToken } }),
    );
  }

  return nextAccessToken;
}

export function refreshAccessTokenSingleFlight() {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function ensureValidSession() {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (!accessToken && !refreshToken) {
    return false;
  }

  if (accessToken && !isTokenExpired(accessToken) && !isTokenExpiringSoon(accessToken)) {
    return true;
  }

  if (!refreshToken) {
    return false;
  }

  try {
    await refreshAccessTokenSingleFlight();
    return true;
  } catch {
    return false;
  }
}

export async function logoutSession() {
  const refreshToken = getRefreshToken();
  try {
    if (refreshToken) {
      await axios.post(
        `${getApiBaseUrl()}/auth/logout`,
        { refreshToken },
        { headers: { 'X-Client-Platform': 'web' } },
      );
    }
  } catch {
    // still clear local session
  }
  clearSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('userLogout'));
  }
}
